import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { LinearProgress, Box, useTheme } from '@mui/material';
import './MockScoreBoard.css';

interface QuizStats {
  totalQuizzes: number;
  totalQuestions: number;
  totalCorrect: number;
  totalWrong: number;
  totalSkipped: number;
  subject: string;
  averagePercentage: string;
  topics: string[];
}

interface ApiResponse {
  success: boolean;
  stats: QuizStats[];
}

// Student fetch — own stats
const fetchStudentQuizStats = async (): Promise<ApiResponse> => {
  const token = localStorage.getItem('token');
  const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/portal/quiz-stats`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

// Parent fetch — child stats via parent endpoint
const fetchChildQuizStats = async (childId: string): Promise<ApiResponse> => {
  const token = localStorage.getItem('token');
  const { data } = await axios.get(
    `${process.env.REACT_APP_API_URL}/api/parent/children/${childId}/quiz-stats`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

const ProgressBar: React.FC<{ value: number; color: string }> = ({ value, color }) => {
  const theme = useTheme();
  return (
    <Box sx={{ width: '100%', mr: 1 }}>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          height: 10,
          borderRadius: 5,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.08)',
          '& .MuiLinearProgress-bar': {
            borderRadius: 5,
            backgroundColor: color,
          },
        }}
      />
    </Box>
  );
};

const MockScoreBoard: React.FC = () => {
  const navigate = useNavigate();
  const { childId } = useParams<{ childId?: string }>();
  const isParentView = Boolean(childId);

  // Student query (used only when no childId in URL)
  const studentQuery = useQuery<ApiResponse>({
    queryKey: ['quizStats', 'student'],
    queryFn: fetchStudentQuizStats,
    enabled: !isParentView,
  });

  // Parent query (used only when childId present in URL)
  const parentQuery = useQuery<ApiResponse>({
    queryKey: ['quizStats', 'child', childId],
    queryFn: () => fetchChildQuizStats(childId!),
    enabled: isParentView,
  });

  const { data, isLoading, isError } = isParentView ? parentQuery : studentQuery;

  const handleCardClick = (subject: string) => {
    if (isParentView) {
      navigate(`/parent/student/${childId}/mock-test/${subject}`);
    } else {
      navigate(`/mock-test/${subject}`);
    }
  };

  if (isLoading) {
    return (
      <div className="scoreboard-container">
        <h1 className="scoreboard-title">Loading...</h1>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="scoreboard-container">
        <h1 className="scoreboard-title">Error loading quiz statistics</h1>
      </div>
    );
  }

  return (
    <div className="scoreboard-container">
      <h1 className="scoreboard-title">Mock Test Scoreboard</h1>
      <div className="scoreboard-grid">
        {data?.stats.map((stat: QuizStats, index: number) => (
          <div
            className="score-card"
            key={index}
            onClick={() => handleCardClick(stat.subject)}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-content">
              <div className="left-section">
                <div className="subject-wrapper">
                  <span className="label">Subject:</span>
                  <span className="subject">{stat.subject}</span>
                </div>
                <div className="percentage-wrapper">
                  <span className="label">Average Percentage:</span>
                  <span className="score-value">{stat.averagePercentage}%</span>
                </div>
                <div className="quiz-info">
                  <span className="label">Total Quizzes: {stat.totalQuizzes}</span>
                  <span className="label">Questions: {stat.totalQuestions}</span>
                </div>
              </div>
              <div className="right-section">
                <div className="progress-section">
                  <div className="progress-item">
                    <div className="progress-header">
                      <span>Correct ({stat.totalCorrect})</span>
                      <span>
                        {stat.totalQuestions
                          ? ((stat.totalCorrect / stat.totalQuestions) * 100).toFixed(1)
                          : 0}
                        %
                      </span>
                    </div>
                    <ProgressBar
                      value={
                        stat.totalQuestions
                          ? (stat.totalCorrect / stat.totalQuestions) * 100
                          : 0
                      }
                      color="#10b981"
                    />
                  </div>
                  <div className="progress-item">
                    <div className="progress-header">
                      <span>Wrong ({stat.totalWrong})</span>
                      <span>
                        {stat.totalQuestions
                          ? ((stat.totalWrong / stat.totalQuestions) * 100).toFixed(1)
                          : 0}
                        %
                      </span>
                    </div>
                    <ProgressBar
                      value={
                        stat.totalQuestions
                          ? (stat.totalWrong / stat.totalQuestions) * 100
                          : 0
                      }
                      color="#ef4444"
                    />
                  </div>
                  <div className="progress-item">
                    <div className="progress-header">
                      <span>Skipped ({stat.totalSkipped})</span>
                      <span>
                        {stat.totalQuestions
                          ? ((stat.totalSkipped / stat.totalQuestions) * 100).toFixed(1)
                          : 0}
                        %
                      </span>
                    </div>
                    <ProgressBar
                      value={
                        stat.totalQuestions
                          ? (stat.totalSkipped / stat.totalQuestions) * 100
                          : 0
                      }
                      color="#f59e0b"
                    />
                  </div>
                </div>
                {stat.topics.length > 0 && (
                  <div className="topics-section">
                    <span className="label">Topics:</span>
                    <div className="topics-list">
                      {stat.topics.slice(0, 3).map((topic: string, i: number) => (
                        <span key={i} className="topic-tag">
                          {topic}
                        </span>
                      ))}
                      {stat.topics.length > 3 && (
                        <span className="topic-tag">+{stat.topics.length - 3} more</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MockScoreBoard;
